import { BybButton } from "@Components/common/byb-ds";
import BybVrDialog, { BybVrDialogContent, BybVrDialogFooter, BybVrDialogHeader } from "@Components/game/common/dialog/byb-vr-dialog";
import PhaseOfPlay from "@Enums/PhaseOfPlay";
import { usePhasesSelection } from "@hooks/game/use-phases-selection";
import useI18n from "@i18n";
import { Button, Stack, Typography, useTheme } from "@mui/material";

interface IPhasesDialog {
  open: boolean;
  selectedPhases: PhaseOfPlay[];
  onClose: () => void;
  onConfirm: (phases: PhaseOfPlay[]) => void;
}

const PhasesDialog = ({ open, selectedPhases, onClose, onConfirm }: IPhasesDialog) => {
  const { i18n } = useI18n();
  const theme = useTheme();

  const { draftPhases, handlePhaseClick, handleSave, handleCancel } = usePhasesSelection(selectedPhases, onConfirm, onClose);

  const handleSaveAndClose = () => {
    handleSave();
    onClose();
  };

  return (
    <BybVrDialog open={open} onClose={handleCancel} name={"phases-selector"} width="40vw">
      <BybVrDialogHeader>
        <Typography variant="text3xl800" color="dsBase.v200" width="100%">
          {i18n("position.selection.select.phases")}
        </Typography>
      </BybVrDialogHeader>

      <BybVrDialogContent>
        <Stack spacing={2} padding={theme.spacing(4)}>
          {/* Individual phases */}
          {Object.values(PhaseOfPlay).map(phase => {
            const isSelected = draftPhases.includes(phase);
            return (
              <Stack
                key={phase}
                onClick={() => handlePhaseClick(phase)}
                sx={{
                  padding: theme.spacing(2, 3),
                  border: `${theme.spacing(0.25)} solid ${isSelected ? theme.palette.dsPrimary.v300 : theme.palette.dsSurface.v300}`,
                  borderRadius: theme.spacing(2),
                  backgroundColor: isSelected ? theme.palette.dsPrimary.v300 : "transparent",
                  cursor: "pointer",
                  transition: "all 0.3s ease-in-out",
                  "&:hover": {
                    borderColor: isSelected ? theme.palette.dsPrimary.v100 : theme.palette.dsSurface.v400,
                    backgroundColor: isSelected ? theme.palette.dsPrimary.v300 : theme.palette.dsSurface.v200,
                  },
                  textAlign: "center",
                }}
              >
                <Typography variant="text1xl500" color={isSelected ? "dsPrimary.v200" : "dsBase.v200"}>
                  {i18n(`phase.${phase}`)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>
      </BybVrDialogContent>

      <BybVrDialogFooter>
        <Button onClick={handleSaveAndClose} sx={{ width: "100%", padding: theme.spacing(2) }}>
          {i18n("save")}
        </Button>
      </BybVrDialogFooter>
    </BybVrDialog>
  );
};

export default PhasesDialog;
